import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Button,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";

import {
  Add,
  CheckCircle,
  Close,
  Delete,
  DeleteOutline,
  EditSquare,
  Memory,
  QueryBuilder,
  ReportProblem,
  Search,
  VisibilityOutlined,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import OtaAddModel from "./models/OtaAddModel";
import OtaEditModel from "./models/OtaEditModel";
import axios from "axios";

import { APP_API_V1_BASE } from "../../config/api";
const TabPanel = ({ value, tabValue, children }) => {
  if (value !== tabValue) return null;
  return <Box mt={3}>{children}</Box>;
};

const fetchFirmwareList = async ({
  deviceType,
  status,
  limit = 20,
  offset = 0,
}) => {
  const token = localStorage.getItem("authToken");
  if (!token) {
    throw new Error("AUTH_TOKEN_MISSING");
  }

  const orgId = localStorage.getItem("activeOrgId");
  const res = await axios.get(
    `${APP_API_V1_BASE}/firmware/packages`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(orgId && { "X-Org-ID": orgId }),
      },
    },
  );

  const allRows = Array.isArray(res.data) ? res.data : [];
  const mapped = allRows.map((f) => ({
    id: f.id,
    created_at: f.created_at,
    version: f.version,
    device_type: f.device_type_id,
    hardware_revision: [],
    checksum_type: "SHA256",
    status: f.is_active ? "ACTIVE" : "PAUSED",
    uploaded_by: "-",
  }));

  const filtered = mapped.filter((row) => {
    const byDevice = !deviceType || row.device_type === deviceType;
    const byStatus = !status || row.status === status;
    return byDevice && byStatus;
  });

  return filtered.slice(offset, offset + limit);
};

function OtaUpdates() {
  const [registeredDevices, setRegisteredDevices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile]);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null); // "add" | "edit"
  const [selectedRow, setSelectedRow] = useState(null);

  const handleAdd = () => {
    setMode("add");
    setSelectedRow(null);
    setOpen(true);
  };

  const handleEdit = (params) => {
    if (params.field === "__check__") return; // ignore checkbox
    setMode("edit");
    setSelectedRow(params.row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setMode(null);
    setSelectedRow(null);
  };

  // const handleUpdatePackage = (updatedRow) => {
  //   setRegisteredDevices((prev) =>
  //     prev.map((row) =>
  //       row.id === updatedRow.id ? { ...row, ...updatedRow } : row,
  //     ),
  //   );
  // };

  const columns = [
    {
      field: "created_at",
      headerName: "Created Time",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const value = params.row?.created_at;
        if (!value) return "-";
        return new Date(value).toLocaleString();
      },
    },
    {
      field: "version",
      headerName: "Version",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "device_type",
      headerName: "Device Type",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "hardware_revision",
      headerName: "HW Revision",
      flex: 1,
      minWidth: 220,
      valueGetter: (params) =>
        params.value?.length ? params.value.join(", ") : "All",
    },
    {
      field: "checksum_type",
      headerName: "Checksum",
      width: 140,
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      renderCell: (params) => {
        const colorMap = {
          DRAFT: "default",
          ACTIVE: "success",
          PAUSED: "warning",
          WITHDRAWN: "error",
        };

        return (
          <Chip
            size="small"
            label={params.value}
            color={colorMap[params.value] || "default"}
          />
        );
      },
    },
    {
      field: "uploaded_by",
      headerName: "Uploaded By",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            disabled={params.row.status !== "DRAFT"} // 🔒 safety
            sx={{
              color: "#9CA3AF",
              "&:hover": {
                color: "#EF4444",
                backgroundColor: "#FEE2E2",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              // TODO: call WITHDRAW API (do NOT mutate UI state directly)
              console.log("Withdraw firmware:", params.row.id);
            }}
          >
            <DeleteOutline />
          </IconButton>
        </Box>
      ),
    },
  ];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [pageModel, setPageModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [statusFilter, setStatusFilter] = useState(null);
  const [deviceTypeFilter, setDeviceTypeFilter] = useState(null);

  useEffect(() => {
    const loadFirmware = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await fetchFirmwareList({
          status: statusFilter,
          deviceType: deviceTypeFilter,
          limit: pageModel.pageSize,
          offset: pageModel.page * pageModel.pageSize,
        });

        // DataGrid requires `id`
        setRows(
          data.map((f) => ({
            id: f.id,
            ...f,
          })),
        );
      } catch (err) {
        console.error(err);

        if (err.response?.status === 401) {
          setError("Session expired. Please login again.");
        } else if (err.response?.status === 403) {
          setError("You do not have permission to view firmware.");
        } else {
          setError("Failed to load firmware.");
        }
      } finally {
        setLoading(false);
      }
    };

    loadFirmware();
  }, [pageModel, statusFilter, deviceTypeFilter, refreshKey]);

  // const filterByDeviceType = (list, deviceType) => {
  //   if (deviceType === "All Device Types") return list;

  //   const keywordMap = {
  //     sensor: "sensor",
  //     gateway: "gateway",
  //     controller: "controller",
  //     camera: "camera",
  //   };

  //   const keyword = keywordMap[deviceType];

  //   return list.filter((item) =>
  //     item.deviceType.toLowerCase().includes(keyword),
  //   );
  // };

  // useEffect(() => {
  //   setFilteredLogs(filterByDeviceType(firmwareList, deviceType));
  //   setFilteredDeployments(filterByDeviceType(deploymentHistory, deviceType));
  // }, [deviceType]);
  // useEffect(() => {
  //   let data = [...firmwareList];

  //   if (deviceType !== "All Device Types") {
  //     data = data.filter((item) => {
  //       const type = item.deviceType.toLowerCase();

  //       if (deviceType === "sensor") {
  //         return type.includes("sensor");
  //       }

  //       if (deviceType === "gateway") {
  //         return type.includes("gateway");
  //       }

  //       if (deviceType === "controller") {
  //         return type.includes("controller");
  //       }
  //       if (deviceType === "camera") {
  //         return type.includes("camera");
  //       }

  //       return true;
  //     });
  //   }

  //   setFilteredLogs(data);
  // }, [deviceType]);

  // const handleChange = (event, newValue) => {
  //   setValue(newValue);
  // };

  return (
    <>
      <Box p={isMobile ? 1 : 0} mt={isMobile ? "2rem" : 0}>
        {/* ================= HEADER ================= */}
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            gap: 2,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Box display={{ xs: "none", md: "flex" }} alignItems="center" gap={2}>
            <Typography fontSize={{ xs: "16px", md: "20px" }} fontWeight={600}>
              OTA Updates
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              // flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              width: { xs: "100%", md: "auto" },
            }}
          >
            <IconButton
              onClick={handleAdd}
              sx={{
                borderRadius: 5,
                color: "#9CA3AF",
                "&:hover": {
                  // color: "#2563eb",
                  backgroundColor: "#f9fafb",
                },
              }}
            >
              <Add />
            </IconButton>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: showSearch ? "1px solid #e5e7eb" : "none",
                borderRadius: "999px",
                px: showSearch ? 1 : 0,
                transition: "all 0.3s ease",
                // backgroundColor: "#f9fafb",
              }}
            >
              <IconButton
                onClick={() => setShowSearch((prev) => !prev)}
                sx={{ color: "#9CA3AF" }}
              >
                <Search />
              </IconButton>

              {showSearch && (
                <>
                  <input
                    autoFocus
                    placeholder="Search OTA packages..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      width: isMobile ? "150px" : "180px",
                      fontSize: "14px",
                      padding: "4px 8px",
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      setShowSearch(false);
                      setSearchText("");
                    }}
                  >
                    <Close />
                  </IconButton>
                </>
              )}
            </Box>

            {selected.length > 0 && (
              <IconButton color="error" borderRadius="5">
                <Delete />
              </IconButton>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            mt: 3,
            height: 400,
            border: "1px solid #e5e7eb",
            borderRadius: 2,
            overflowX: "auto",
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            onRowClick={handleEdit}
            // 🔑 server-side pagination
            paginationMode="server"
            paginationModel={pageModel}
            onPaginationModelChange={setPageModel}
            pageSizeOptions={[5, 10, 20]}
            disableRowSelectionOnClick
            disableColumnMenu
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f9fafb",
                fontWeight: 600,
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f9fafb",
              },
            }}
            localeText={{
              noRowsLabel: "No firmware uploaded yet",
            }}
          />
        </Box>
      </Box>
      {open && mode === "add" && (
        <OtaAddModel
          key="add-modal" // forces remount
          open={open}
          onClose={handleClose}
          onSave={() => setRefreshKey((prev) => prev + 1)}
        />
      )}

      {open && mode === "edit" && (
        <OtaEditModel
          key={selectedRow?.id} // forces remount
          open={open}
          onClose={handleClose}
          rowData={selectedRow}
          onSave=""
        />
      )}
    </>
  );
}

export default OtaUpdates;
